import { useReactFlow } from '@xyflow/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  CallDistributionNodeData,
  CallDistributionOption,
} from '../../../converters/shared/reactFlowTypes'

/**
 * Hook for Call Distribution node functionality.
 * Handles timeout and weight options that must total 100%.
 */
export const useCallDistributionNode = (
  id: string,
  data: CallDistributionNodeData,
) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)
  const isUpdatingRef = useRef(false)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  // Local state for timeout input (to prevent focus loss)
  const [timeout, setTimeoutValue] = useState(
    data.timeout !== undefined ? String(data.timeout) : '20',
  )

  // Sync local state from external data (only when not updating)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      setTimeoutValue(data.timeout !== undefined ? String(data.timeout) : '20')
    }
  }, [data.timeout])

  // Use options from data, or empty array if none exist (don't add defaults)
  // The UI will show the "+ Add Option" button when options array is empty
  const currentOptions = React.useMemo(() => {
    if (
      data.options &&
      Array.isArray(data.options) &&
      data.options.length > 0
    ) {
      return data.options
    }
    // Return empty array - let user add options via UI
    return []
  }, [data.options])

  // Local state for weight inputs (to prevent focus loss)
  const [weightInputs, setWeightInputs] = useState<Record<number, string>>(
    () => {
      const weights: Record<number, string> = {}
      currentOptions.forEach((option, index) => {
        const weightValue =
          option.weight !== undefined ? String(option.weight) : ''
        weights[index] = weightValue
      })
      return weights
    },
  )

  // Sync local weight state from external data (only when not updating)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      const weights: Record<number, string> = {}
      currentOptions.forEach((option, index) => {
        weights[index] =
          option.weight !== undefined ? String(option.weight) : ''
      })
      setWeightInputs(weights)
    }
  }, [currentOptions])

  // Initialize default options if they don't exist (only once)
  useEffect(() => {
    if (!data.options || data.options.length === 0) {
      const defaultOptions = [{ weight: 50 }, { weight: 50 }]
      // Only update if we haven't already set defaults
      if (!dataRef.current.options || dataRef.current.options.length === 0) {
        updateNodeData(id, {
          ...dataRef.current,
          options: defaultOptions,
        })
      }
    }
  }, [id, data.options, updateNodeData])

  const handleTimeoutChange = useCallback((value: string) => {
    isUpdatingRef.current = true
    setTimeoutValue(value)
  }, [])

  const handleTimeoutBlur = useCallback(() => {
    const timeoutValue = timeout ? parseInt(timeout, 10) : undefined
    // Clamp to valid range: 2-40
    const clampedValue =
      timeoutValue && !isNaN(timeoutValue)
        ? Math.max(2, Math.min(40, timeoutValue))
        : undefined
    updateNodeData(id, {
      ...dataRef.current,
      timeout: clampedValue || undefined,
    })
    isUpdatingRef.current = false
  }, [id, updateNodeData, timeout])

  const handleWeightChange = useCallback((index: number, value: string) => {
    isUpdatingRef.current = true
    setWeightInputs((prev) => ({
      ...prev,
      [index]: value,
    }))
  }, [])

  const handleWeightBlur = useCallback(
    (index: number) => {
      const weightValue = weightInputs[index]
        ? parseInt(weightInputs[index], 10)
        : 1
      // Clamp to valid range: 1-100
      const clampedWeight = isNaN(weightValue)
        ? 1
        : Math.max(1, Math.min(100, weightValue))
      const updatedOptions = [...currentOptions]
      updatedOptions[index] = {
        ...updatedOptions[index],
        weight: clampedWeight,
      }
      updateNodeData(id, {
        ...dataRef.current,
        options: updatedOptions,
      })
      isUpdatingRef.current = false
    },
    [id, updateNodeData, currentOptions, weightInputs],
  )

  const handleOptionsChange = useCallback(
    (options: CallDistributionOption[]) => {
      updateNodeData(id, {
        ...dataRef.current,
        options,
      })
    },
    [id, updateNodeData],
  )

  // Calculate total weight percentage using local state for real-time validation
  const totalWeight = Object.values(weightInputs).reduce((sum, weightStr) => {
    const weight = weightStr ? parseInt(weightStr, 10) : 1
    return sum + (isNaN(weight) ? 1 : Math.max(1, weight))
  }, 0)

  // CRITICAL: Must be exactly 100% (matches Legacy behavior)
  // Legacy: if (percentage > 100 || percentage < 100) { /* invalid */ }
  // This prevents saving when total != 100%
  const isWeightValid = totalWeight === 100

  return {
    timeout,
    currentOptions,
    weightInputs,
    totalWeight,
    isWeightValid,
    handleTimeoutChange,
    handleTimeoutBlur,
    handleWeightChange,
    handleWeightBlur,
    handleOptionsChange,
    hasData: !!timeout || currentOptions.length > 0,
  }
}
